import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserAdmin } from 'src/app/core/models/admin';
import { Dictionary } from 'src/app/core/models/common/generic.type.model';
import { AddOnDemandPickupRequest } from 'src/app/core/models/operations/operations-on-demand-pickup.model';
import { SaveScheduledPickupRequest } from 'src/app/core/models/operations/operations-pickup.model';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { AddScheduledPickupData } from './add-pickup-request.constants';

@Component({
  selector: 'app-add-pickup-request',
  templateUrl: './add-pickup-request.component.html',
  styleUrls: ['./add-pickup-request.component.scss'],
  standalone: false,
})
export class AddPickupRequestComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);

  currentUser!: UserAdmin;
  routes!: string[];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      form: FormGroup;
      routes: string[];
      countryCode: string;
    },
    private dialogRef: MatDialogRef<AddPickupRequestComponent>,
    private datePipe: DatePipe,
    private pickupAssignmentsService: PickupAssignmentsService,
    private commonUtils: CommonUtils,
  ) {}

  ngOnInit() {
    this.routes = this.data.routes;
    this.userService
      .getCurrentUser$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((user) => {
        this.currentUser = user;
      });
  }

  getSubmittedForm(event: Dictionary<string>) {
    if (event.ondemandPickUp) {
      this.addOnDemandPickup(event);
    } else {
      this.addScheduledPickup(event as AddScheduledPickupData);
    }
  }

  addScheduledPickup(event: AddScheduledPickupData) {
    const startDate = this.datePipe.transform(event.startDate, 'MM/dd/yyyy');
    const endDate = this.datePipe.transform(event.endDate, 'MM/dd/yyyy');
    const interruptDate = this.datePipe.transform(
      event.interruptDate,
      'MM/dd/yyyy',
    );
    const restartDate = this.datePipe.transform(
      event.restartDate,
      'MM/dd/yyyy',
    );

    const addScheduledPickupRequest: SaveScheduledPickupRequest = {
      schedulePickup: {
        id: 0,
        ondemandPickUp: false,
        daysAssigned: {
          sunday: event.daysAssigned?.sunday,
          monday: event.daysAssigned?.monday,
          tuesday: event.daysAssigned?.tuesday,
          wednesday: event.daysAssigned?.wednesday,
          thursday: event.daysAssigned?.thursday,
          friday: event.daysAssigned?.friday,
          saturday: event.daysAssigned?.saturday,
        },
        pickupRequest: event.pickupRequestNumber,
        pickupPoint: event.pickupPoint,
        route: event.route,
        startDate: startDate ?? '',
        endDate: endDate ?? '',
        readyTime: event.readyTime,
        commitTime: event.commitTime,
        closingTime: event.closingTime,
        interruptDate: interruptDate ?? '',
        restartDate: restartDate ?? '',
        customerInfo: {
          shipperNo: event.shipperNumber,
          customerName: event.customerName,
          contactName: event.contactName,
          contactNumber: event.contactNumber,
          prefix: event.prefix,
          streetName: event.streetName,
          streetAddress: event.streetAddress,
          suffix: event.suffix,
          suite: event.suite,
          floor: event.floor,
          cityTown: event.city,
          province: event.province,
          postalCode: event.postalCode?.toUpperCase() ?? '',
          latitude: event.latitude,
          longitude: event.longitude,
        },
        SSCDescription: event.sscDescription,
        additionalInfo: event.additionalInfo,
      },
    };
    this.pickupAssignmentsService
      .saveScheduledPickup(
        addScheduledPickupRequest,
        this.currentUser?.location,
      )
      .subscribe((response) => {
        if (response.code === 200) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }
        this.dialogRef.close({
          response: response,
          data: addScheduledPickupRequest,
        });
      });
  }

  addOnDemandPickup(event: Dictionary<string>) {
    const startDate = this.datePipe.transform(event.startDate, 'yyyy-MM-dd');
    const addOnDemandRequest: AddOnDemandPickupRequest = {
      onDemand: event.ondemandPickUp,
      startDate: startDate ?? '',
      shippingNumber: event.shipperNumber,
      customerName: event.customerName,
      contactName: event.contactName,
      phoneNumber: event.contactNumber,
      streetAddress: event.streetAddress,
      streetName: event.streetName,
      streetType: event.streetType,
      politicalDivision1: event.province,
      politicalDivision2: event.city,
      room: event.suite,
      floor: event.floor,
      postalCode: event.postalCode?.toUpperCase(),
      readyTime: event.readyTime,
      commitTime: event.commitTime,
      closingtime: event.closingTime,
      comments: event.additionalInfo,
      latitude: event.latitude,
      longitude: event.longitude,
      pickupPoint: event.pickupPoint,
      orderType: '',
      route: event.route,
      streetPrefix: event.prefix,
      streetSuffix: event.suffix,
      sscCodes: event.sscDescription,
      prnNr: event.pickupRequestNumber,
    };

    this.pickupAssignmentsService
      .addOnDemandPickup(addOnDemandRequest, this.currentUser.location)
      .subscribe((response) => {
        if (response.code === 200) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }
        this.dialogRef.close({
          response: response,
          data: addOnDemandRequest,
        });
      });
  }
}
